import maplibregl from 'maplibre-gl';
import jsPDF from 'jspdf';

/**
 * Generates a PNG data URL from the map canvas.
 * @param map - The MapLibre GL JS map instance.
 * @returns A Promise that resolves with the PNG data URL string.
 */
/**
 * Exports the map view to a PNG data URL using a temporary hidden map instance
 * configured with the specified DPI, dimensions, style, camera, sources, and layers.
 * @param options - Configuration options for the export.
 * @returns A Promise that resolves with the PNG data URL string.
 */
export const exportMapToPng = async (map: maplibregl.Map): Promise<string> => {
    return new Promise((resolve, reject) => {
        try {
            // Ensure the map is fully rendered before exporting
            map.once('render', () => {
                try { // Added inner try-catch for toDataURL
                    const canvas = map.getCanvas();
                    const dataUrl = canvas.toDataURL('image/png');
                    resolve(dataUrl);
                } catch (innerError) {
                    console.error("Error calling toDataURL:", innerError);
                    reject("Failed to get map canvas data.");
                }
            });
            // Trigger a render in case the map is idle
            map.triggerRepaint();
        } catch (error) {
            console.error("Error setting up map export to PNG:", error);
            reject("Failed to export map to PNG.");
        }
    });
};


/**
 * Generates a PDF document containing the map image (client-side).
 * @param pngDataUrl - The PNG data URL generated from the map canvas.
 * @param title - Optional title for the map within the PDF.
 * @param format - The paper format (e.g., 'a4', 'a3', 'letter'). Default 'a4'.
 * @param orientation - The paper orientation ('p' for portrait, 'l' for landscape). Default 'p'.
 * @param marginMm - The margin in millimeters to apply on all sides. Default 10.
 * @returns A Promise that resolves when the PDF is generated (but not yet downloaded).
 */
export const exportMapToPdfClientSide = async (
    pngDataUrl: string,
    title?: string,
    format: 'a4' | 'a3' | 'letter' = 'a4',
    orientation: 'p' | 'l' = 'p',
    marginMm: number = 10 // Add margin parameter with default
): Promise<jsPDF> => {
    return new Promise((resolve, reject) => {
        try {
            const pdf = new jsPDF({
                orientation: orientation,
                unit: 'mm',
                format: format,
            });

            const imgProps = pdf.getImageProperties(pngDataUrl);
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            // Calculate image dimensions to fit the page while maintaining aspect ratio
            // const margin = 10; // Use the provided marginMm instead
            const availableWidth = pdfWidth - 2 * marginMm;
            const availableHeight = pdfHeight - 2 * marginMm - (title ? 10 : 0); // Extra space for title

            const imgRatio = imgProps.width / imgProps.height;
            const availableRatio = availableWidth / availableHeight;

            let imgWidth, imgHeight;
            if (imgRatio > availableRatio) {
                // Image is wider than available space
                imgWidth = availableWidth;
                imgHeight = imgWidth / imgRatio;
            } else {
                // Image is taller than available space
                imgHeight = availableHeight;
                imgWidth = imgHeight * imgRatio;
            }

            
                        const x = marginMm + (availableWidth - imgWidth) / 2; // Use marginMm
                        const y = marginMm + (title ? 10 : 0); // Start lower if there's a title, use marginMm
             // Add title if provided
             if (title) {
                pdf.setFontSize(12);
                pdf.text(title, pdfWidth / 2, marginMm + 5, { align: 'center' }); // Use marginMm
            }

            // Add the image
            pdf.addImage(pngDataUrl, 'PNG', x, y, imgWidth, imgHeight);

            resolve(pdf);
        } catch (error) {
            console.error("Error exporting map to PDF:", error);
            reject("Failed to export map to PDF.");
        }
    });
};


/**
 * Triggers a browser download for a file.
 * @param dataUrl - The data URL (e.g., for PNG) or Blob object.
 * @param filename - The desired filename for the download.
 */
export const downloadFile = (dataUrl: string | Blob, filename: string): void => {
  const link = document.createElement('a');
  if (typeof dataUrl === 'string') {
      link.href = dataUrl;
  } else {
      link.href = URL.createObjectURL(dataUrl);
  }
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  if (typeof dataUrl !== 'string') {
      URL.revokeObjectURL(link.href); // Clean up Blob URL
  }
};